import React, { useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/Card';
import { Badge } from '../../components/ui/Badge';
import { Button } from '../../components/ui/Button';
import { ArrowLeft, Building2, Users, Calendar, Activity, Settings, FileText, Image, StickyNote, Link as LinkIcon, ExternalLink, Upload, Plus, Trash2, Check, Save, FolderOpen, MoreHorizontal } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
type ProjectFile = {
  id: string;
  name: string;
  size: string;
  date: string;
};
type ProjectLink = {
  id: string;
  label: string;
  url: string;
};
const projectData: Record<string, any> = {
  '1': {
    id: 1,
    name: 'The Riverside Collection',
    builder: 'Excel Homes',
    builderId: 1,
    status: 'Active',
    buyers: 124,
    createdDate: '2023-06-15',
    lastActivity: '2 hours ago',
    description: 'Luxury waterfront homes with modern amenities and stunning river views.',
    models: 3,
    activeOptions: 245,
    totalOptions: 312,
    gdn: '963258741'
  },
  '2': {
    id: 2,
    name: 'Coastal Living',
    builder: 'Schell Brothers',
    builderId: 2,
    status: 'Active',
    buyers: 234,
    createdDate: '2023-03-22',
    lastActivity: '1 hour ago',
    description: 'Beachside community featuring contemporary coastal architecture.',
    models: 4,
    activeOptions: 189,
    totalOptions: 256,
    gdn: '741852963'
  },
  '3': {
    id: 3,
    name: 'Summit Ridge',
    builder: 'Mattamy Homes',
    builderId: 3,
    status: 'Active',
    buyers: 312,
    createdDate: '2022-11-10',
    lastActivity: '30 mins ago',
    description: 'Mountain-view estate homes with premium finishes and spacious layouts.',
    models: 5,
    activeOptions: 298,
    totalOptions: 387,
    gdn: '852963741'
  }
};
const activityData = [{
  date: 'Mon',
  buyers: 18,
  sessions: 45
}, {
  date: 'Tue',
  buyers: 22,
  sessions: 52
}, {
  date: 'Wed',
  buyers: 15,
  sessions: 38
}, {
  date: 'Thu',
  buyers: 28,
  sessions: 67
}, {
  date: 'Fri',
  buyers: 19,
  sessions: 48
}, {
  date: 'Sat',
  buyers: 12,
  sessions: 29
}, {
  date: 'Sun',
  buyers: 10,
  sessions: 21
}];
const modelsList = [{
  id: 1,
  name: 'The Oakwood',
  status: 'Active',
  buyers: 54,
  options: 87
}, {
  id: 2,
  name: 'The Maple',
  status: 'Active',
  buyers: 41,
  options: 72
}, {
  id: 3,
  name: 'The Cedar',
  status: 'Active',
  buyers: 29,
  options: 65
}];
type TabId = 'info' | 'models' | 'media' | 'documents' | 'notes';
const tabs: {
  id: TabId;
  label: string;
  subtitle: string;
  icon: React.ElementType;
}[] = [{
  id: 'info',
  label: 'Info',
  subtitle: 'Project Information',
  icon: FolderOpen
}, {
  id: 'models',
  label: 'Models',
  subtitle: 'Project Models',
  icon: Building2
}, {
  id: 'media',
  label: 'Media',
  subtitle: 'Project Media Files',
  icon: Image
}, {
  id: 'documents',
  label: 'Documents',
  subtitle: 'Project Document Files',
  icon: FileText
}, {
  id: 'notes',
  label: 'Notes & Links',
  subtitle: 'Internal notes and links',
  icon: StickyNote
}];
function InfoTab({
  project
}: {
  project: any;
}) {
  return <div className="space-y-6" data-id="element-2745">
      <div className="grid grid-cols-2 gap-4" data-id="element-2746">
        <Card data-id="element-2747">
          <CardContent className="flex items-center p-5" data-id="element-2748">
            <div className="p-2.5 bg-primary-50 rounded-lg" data-id="element-2749">
              <Users className="h-5 w-5 text-primary-600" data-id="element-2750" />
            </div>
            <div className="ml-3" data-id="element-2751">
              <p className="text-xs font-medium text-slate-500" data-id="element-2752">
                Active Buyers
              </p>
              <h3 className="text-2xl font-bold text-slate-900" data-id="element-2753">
                {project.buyers}
              </h3>
            </div>
          </CardContent>
        </Card>
        <Card data-id="element-2754">
          <CardContent className="flex items-center p-5" data-id="element-2755">
            <div className="p-2.5 bg-slate-100 rounded-lg" data-id="element-2756">
              <Building2 className="h-5 w-5 text-slate-600" data-id="element-2757" />
            </div>
            <div className="ml-3" data-id="element-2758">
              <p className="text-xs font-medium text-slate-500" data-id="element-2759">Models</p>
              <h3 className="text-2xl font-bold text-slate-900" data-id="element-2760">
                {project.models}
              </h3>
            </div>
          </CardContent>
        </Card>
        <Card data-id="element-2761">
          <CardContent className="flex items-center p-5" data-id="element-2762">
            <div className="p-2.5 bg-slate-100 rounded-lg" data-id="element-2763">
              <Settings className="h-5 w-5 text-slate-600" data-id="element-2764" />
            </div>
            <div className="ml-3" data-id="element-2765">
              <p className="text-xs font-medium text-slate-500" data-id="element-2766">
                Active Options
              </p>
              <h3 className="text-2xl font-bold text-slate-900" data-id="element-2767">
                {project.activeOptions}/{project.totalOptions}
              </h3>
            </div>
          </CardContent>
        </Card>
        <Card data-id="element-2768">
          <CardContent className="flex items-center p-5" data-id="element-2769">
            <div className="p-2.5 bg-slate-100 rounded-lg" data-id="element-2770">
              <Activity className="h-5 w-5 text-slate-600" data-id="element-2771" />
            </div>
            <div className="ml-3" data-id="element-2772">
              <p className="text-xs font-medium text-slate-500" data-id="element-2773">Avg Session</p>
              <h3 className="text-2xl font-bold text-slate-900" data-id="element-2774">18m</h3>
            </div>
          </CardContent>
        </Card>
      </div>
      <Card data-id="element-2775">
        <CardHeader data-id="element-2776">
          <CardTitle data-id="element-2777">Basic Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4" data-id="element-2778">
          <div data-id="element-2779">
            <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-2780">
              Project Name
            </label>
            <input type="text" defaultValue={project.name} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:ring-primary-500 focus:border-primary-500" data-id="element-2781" />
          </div>
          <div data-id="element-2782">
            <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-2783">
              GDN CMS ID
            </label>
            <input type="text" defaultValue={project.gdn} placeholder="Example: 963258741" className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:ring-primary-500 focus:border-primary-500" data-id="element-2784" />
          </div>
          <div data-id="element-2785">
            <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-2786">
              Status
            </label>
            <select defaultValue={project.status} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm bg-white focus:ring-primary-500 focus:border-primary-500" data-id="element-2787">
              <option value="Active" data-id="element-2788">Active</option>
              <option value="Draft" data-id="element-2789">Draft</option>
              <option value="Archived" data-id="element-2790">Archived</option>
            </select>
          </div>
          <div data-id="element-2791">
            <label className="block text-sm font-medium text-slate-700 mb-1.5" data-id="element-2792">
              Description
            </label>
            <textarea defaultValue={project.description} rows={3} className="w-full px-3 py-2 border border-slate-300 rounded-md text-sm focus:ring-primary-500 focus:border-primary-500 resize-none" data-id="element-2793" />
          </div>
          <div className="flex justify-end pt-2" data-id="element-2794">
            <Button leftIcon={<Save className="h-4 w-4" data-id="element-2796" />} data-id="element-2795">
              Save Changes
            </Button>
          </div>
        </CardContent>
      </Card>
      <Card data-id="element-2797">
        <CardHeader data-id="element-2798">
          <CardTitle data-id="element-2799">Activity Overview (Last 7 Days)</CardTitle>
        </CardHeader>
        <CardContent data-id="element-2800">
          <div className="h-[240px] w-full" data-id="element-2801">
            <ResponsiveContainer width="100%" height="100%" data-id="element-2802">
              <LineChart data={activityData} data-id="element-2803">
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" data-id="element-2804" />
                <XAxis dataKey="date" tick={{
                fill: '#64748b',
                fontSize: 12
              }} data-id="element-2805" />
                <YAxis tick={{
                fill: '#64748b',
                fontSize: 12
              }} data-id="element-2806" />
                <Tooltip contentStyle={{
                borderRadius: '8px',
                border: 'none',
                boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1)'
              }} data-id="element-2807" />
                <Line type="monotone" dataKey="buyers" stroke="#06b6d4" strokeWidth={2} dot={{
                fill: '#06b6d4'
              }} name="New Buyers" data-id="element-2808" />
                <Line type="monotone" dataKey="sessions" stroke="#94a3b8" strokeWidth={2} dot={{
                fill: '#94a3b8'
              }} name="Sessions" data-id="element-2809" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
    </div>;
}
function ModelsTab() {
  return <div className="space-y-6" data-id="element-2810">
      <div className="flex items-center justify-between" data-id="element-2811">
        <div data-id="element-2812">
          <h2 className="text-lg font-semibold text-slate-900" data-id="element-2813">Models</h2>
          <p className="text-sm text-slate-500" data-id="element-2814">
            Models assigned to this project
          </p>
        </div>
        <Button leftIcon={<Plus className="h-4 w-4" data-id="element-2816" />} size="sm" data-id="element-2815">
          Add Model
        </Button>
      </div>
      <Card noPadding data-id="element-2817">
        <table className="w-full text-left text-sm" data-id="element-2818">
          <thead className="bg-slate-50 text-slate-500 font-medium border-b border-slate-200" data-id="element-2819">
            <tr data-id="element-2820">
              <th className="px-5 py-3.5" data-id="element-2821">Model Name</th>
              <th className="px-5 py-3.5" data-id="element-2822">Status</th>
              <th className="px-5 py-3.5" data-id="element-2823">Buyers</th>
              <th className="px-5 py-3.5" data-id="element-2824">Options</th>
              <th className="px-5 py-3.5 text-right" data-id="element-2825">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100" data-id="element-2826">
            {modelsList.map(model => <tr key={model.id} className="hover:bg-slate-50 transition-colors" data-id="element-2827">
                <td className="px-5 py-3.5 font-medium text-slate-900" data-id="element-2828">
                  {model.name}
                </td>
                <td className="px-5 py-3.5" data-id="element-2829">
                  <Badge variant={model.status === 'Active' ? 'success' : 'neutral'} data-id="element-2830">
                    {model.status}
                  </Badge>
                </td>
                <td className="px-5 py-3.5 text-slate-600" data-id="element-2831">
                  <div className="flex items-center gap-1.5" data-id="element-2832">
                    <Users className="h-3.5 w-3.5 text-slate-400" data-id="element-2833" />
                    {model.buyers}
                  </div>
                </td>
                <td className="px-5 py-3.5 text-slate-600" data-id="element-2834">{model.options}</td>
                <td className="px-5 py-3.5 text-right" data-id="element-2835">
                  <Button variant="ghost" size="sm" data-id="element-2836">
                    <MoreHorizontal className="h-4 w-4" data-id="element-2837" />
                  </Button>
                </td>
              </tr>)}
          </tbody>
        </table>
      </Card>
    </div>;
}
function FileUploadSection({
  title,
  subtitle,
  files,
  onFilesChange
}: {
  title: string;
  subtitle: string;
  files: ProjectFile[];
  onFilesChange: (files: ProjectFile[]) => void;
}) {
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const handleFileSelect = (selected: FileList | null) => {
    if (!selected) return;
    const newFiles: ProjectFile[] = Array.from(selected).map(f => ({
      id: `f${Date.now()}-${Math.random().toString(36).slice(2, 6)}`,
      name: f.name,
      size: f.size < 1024 * 1024 ? `${(f.size / 1024).toFixed(0)} KB` : `${(f.size / (1024 * 1024)).toFixed(1)} MB`,
      date: new Date().toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric'
      })
    }));
    onFilesChange([...files, ...newFiles]);
  };
  const extColor = (ext: string) => {
    if (ext === 'pdf') return 'bg-red-50 text-red-500';
    if (['xlsx', 'xls'].includes(ext)) return 'bg-emerald-50 text-emerald-500';
    if (['docx', 'doc'].includes(ext)) return 'bg-blue-50 text-blue-500';
    if (['jpg', 'jpeg', 'png', 'webp', 'gif', 'svg'].includes(ext)) return 'bg-violet-50 text-violet-500';
    return 'bg-slate-100 text-slate-400';
  };
  return <div className="space-y-6" data-id="element-2838">
      <div data-id="element-2839">
        <h2 className="text-lg font-semibold text-slate-900" data-id="element-2840">{title}</h2>
        <p className="text-sm text-slate-500" data-id="element-2841">{subtitle}</p>
      </div>
      <Card data-id="element-2842">
        <CardContent className="pt-6" data-id="element-2843">
          <div onDragOver={e => {
          e.preventDefault();
          setIsDragging(true);
        }} onDragLeave={e => {
          e.preventDefault();
          setIsDragging(false);
        }} onDrop={e => {
          e.preventDefault();
          setIsDragging(false);
          handleFileSelect(e.dataTransfer.files);
        }} onClick={() => fileInputRef.current?.click()} className={`border-2 border-dashed rounded-xl p-10 text-center cursor-pointer transition-colors ${isDragging ? 'border-primary-400 bg-primary-50' : 'border-slate-200 hover:border-slate-300 bg-slate-50/50'}`} data-id="element-2844">
            <Upload className={`h-8 w-8 mx-auto mb-3 ${isDragging ? 'text-primary-500' : 'text-slate-300'}`} data-id="element-2845" />
            <p className="text-sm font-medium text-slate-600" data-id="element-2846">
              {isDragging ? 'Drop files here...' : 'Drop files here or click to upload'}
            </p>
            <p className="text-xs text-slate-400 mt-1" data-id="element-2847">
              PDF, XLSX, DOCX, images, and more
            </p>
            <input ref={fileInputRef} type="file" multiple className="hidden" onChange={e => handleFileSelect(e.target.files)} data-id="element-2848" />
          </div>
          {files.length > 0 && <div className="mt-4 space-y-2" data-id="element-2849">
              {files.map(file => {
            const ext = file.name.split('.').pop()?.toLowerCase() || '';
            return <div key={file.id} className="flex items-center gap-3 px-3 py-2.5 bg-slate-50 rounded-lg border border-slate-100 group" data-id="element-2850">
                    <div className={`h-9 w-9 rounded-lg flex items-center justify-center flex-shrink-0 ${extColor(ext)}`} data-id="element-2851">
                      <FileText className="h-4 w-4" data-id="element-2852" />
                    </div>
                    <div className="flex-1 min-w-0" data-id="element-2853">
                      <p className="text-sm font-medium text-slate-800 truncate" data-id="element-2854">
                        {file.name}
                      </p>
                      <p className="text-xs text-slate-400" data-id="element-2855">
                        {file.size} · {file.date}
                      </p>
                    </div>
                    <button onClick={e => {
                e.stopPropagation();
                onFilesChange(files.filter(f => f.id !== file.id));
              }} className="p-1 text-slate-300 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100" data-id="element-2856">
                      <Trash2 className="h-3.5 w-3.5" data-id="element-2857" />
                    </button>
                  </div>;
          })}
            </div>}
        </CardContent>
      </Card>
    </div>;
}
function MediaTab() {
  const [files, setFiles] = useState<ProjectFile[]>([{
    id: 'f1',
    name: 'riverside_hero.jpg',
    size: '3.2 MB',
    date: 'Feb 10, 2024'
  }, {
    id: 'f2',
    name: 'model_a_exterior.png',
    size: '1.8 MB',
    date: 'Feb 12, 2024'
  }]);
  return <FileUploadSection title="Media" subtitle="Images and media files for this project" files={files} onFilesChange={setFiles} data-id="element-2858" />;
}
function DocumentsTab() {
  const [files, setFiles] = useState<ProjectFile[]>([{
    id: 'd1',
    name: 'Riverside_Floorplans_v3.pdf',
    size: '4.1 MB',
    date: 'Jan 28, 2024'
  }, {
    id: 'd2',
    name: 'Option_Pricing_Q1.xlsx',
    size: '920 KB',
    date: 'Feb 1, 2024'
  }]);
  return <FileUploadSection title="Documents" subtitle="Contracts, pricing sheets, and project documents" files={files} onFilesChange={setFiles} data-id="element-2859" />;
}
function NotesTab() {
  const [notes, setNotes] = useState('Primary contact: Jamie R. (jamie@excelhomes.com)\nModel drawings signed off Jan 2024.\nQ1 option pricing locked — do not update until April review.');
  const [notesSaved, setNotesSaved] = useState(false);
  const [links, setLinks] = useState<ProjectLink[]>([{
    id: 'l1',
    label: 'Google Drive - Project Files',
    url: 'https://drive.google.com/drive/folders/example'
  }, {
    id: 'l2',
    label: 'Onboarding Document',
    url: 'https://docs.google.com/document/d/example'
  }]);
  const [showAddLink, setShowAddLink] = useState(false);
  const [newLabel, setNewLabel] = useState('');
  const [newUrl, setNewUrl] = useState('');
  const handleAddLink = () => {
    if (!newLabel.trim() || !newUrl.trim()) return;
    setLinks(prev => [...prev, {
      id: `l${Date.now()}`,
      label: newLabel.trim(),
      url: newUrl.trim()
    }]);
    setNewLabel('');
    setNewUrl('');
    setShowAddLink(false);
  };
  const handleSaveNotes = () => {
    setNotesSaved(true);
    setTimeout(() => setNotesSaved(false), 2000);
  };
  return <div className="space-y-6" data-id="element-2860">
      <div data-id="element-2861">
        <h2 className="text-lg font-semibold text-slate-900" data-id="element-2862">Notes & Links</h2>
        <p className="text-sm text-slate-500" data-id="element-2863">
          Internal notes and external resources for this project
        </p>
      </div>
      <Card data-id="element-2864">
        <CardHeader data-id="element-2865">
          <div className="flex items-center justify-between" data-id="element-2866">
            <div className="flex items-center gap-2" data-id="element-2867">
              <LinkIcon className="h-4 w-4 text-slate-500" data-id="element-2868" />
              <CardTitle className="text-base" data-id="element-2869">External Links</CardTitle>
            </div>
            {!showAddLink && <button onClick={() => setShowAddLink(true)} className="flex items-center gap-1 text-xs font-medium text-primary-600 hover:text-primary-700 transition-colors" data-id="element-2870">
                <Plus className="h-3.5 w-3.5" data-id="element-2871" />
                Add Link
              </button>}
          </div>
        </CardHeader>
        <CardContent data-id="element-2872">
          {showAddLink && <div className="bg-slate-50 border border-slate-200 rounded-lg p-3 mb-4 space-y-2" data-id="element-2873">
              <input type="text" value={newLabel} onChange={e => setNewLabel(e.target.value)} placeholder="Label (e.g. Google Drive - Project Files)" className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:ring-primary-500 focus:border-primary-500" autoFocus data-id="element-2874" />
              <input type="url" value={newUrl} onChange={e => setNewUrl(e.target.value)} placeholder="https://..." className="w-full px-3 py-2 text-sm border border-slate-300 rounded-md focus:ring-primary-500 focus:border-primary-500" data-id="element-2875" />
              <div className="flex justify-end gap-2" data-id="element-2876">
                <Button variant="ghost" size="sm" onClick={() => {
              setShowAddLink(false);
              setNewLabel('');
              setNewUrl('');
            }} data-id="element-2877">
                  Cancel
                </Button>
                <Button size="sm" onClick={handleAddLink} disabled={!newLabel.trim() || !newUrl.trim()} leftIcon={<Plus className="h-3.5 w-3.5" data-id="element-2879" />} data-id="element-2878">
                  Save Link
                </Button>
              </div>
            </div>}
          <div className="space-y-2" data-id="element-2880">
            {links.length === 0 && !showAddLink && <div className="text-center py-8 text-slate-400" data-id="element-2881">
                <LinkIcon className="h-7 w-7 mx-auto mb-2 opacity-40" data-id="element-2882" />
                <p className="text-xs" data-id="element-2883">No links added yet</p>
              </div>}
            {links.map(link => <div key={link.id} className="flex items-center gap-2 px-3 py-2.5 bg-slate-50 rounded-lg border border-slate-100 group" data-id="element-2884">
                <ExternalLink className="h-4 w-4 text-slate-400 flex-shrink-0" data-id="element-2885" />
                <div className="flex-1 min-w-0" data-id="element-2886">
                  <p className="text-sm font-medium text-slate-800 truncate" data-id="element-2887">
                    {link.label}
                  </p>
                  <p className="text-xs text-slate-400 truncate" data-id="element-2888">{link.url}</p>
                </div>
                <a href={link.url} target="_blank" rel="noopener noreferrer" className="p-1 text-slate-400 hover:text-primary-600 transition-colors flex-shrink-0" data-id="element-2889">
                  <ExternalLink className="h-3.5 w-3.5" data-id="element-2890" />
                </a>
                <button onClick={() => setLinks(prev => prev.filter(l => l.id !== link.id))} className="p-1 text-slate-300 hover:text-red-500 transition-colors opacity-0 group-hover:opacity-100 flex-shrink-0" data-id="element-2891">
                  <Trash2 className="h-3.5 w-3.5" data-id="element-2892" />
                </button>
              </div>)}
          </div>
        </CardContent>
      </Card>
      <Card data-id="element-2893">
        <CardHeader data-id="element-2894">
          <div className="flex items-center gap-2" data-id="element-2895">
            <StickyNote className="h-4 w-4 text-slate-500" data-id="element-2896" />
            <CardTitle className="text-base" data-id="element-2897">Internal Notes</CardTitle>
          </div>
        </CardHeader>
        <CardContent data-id="element-2898">
          <textarea value={notes} onChange={e => {
          setNotes(e.target.value);
          setNotesSaved(false);
        }} rows={8} placeholder="Add internal notes about this project..." className="w-full px-3 py-2.5 text-sm border border-slate-200 rounded-lg focus:ring-primary-500 focus:border-primary-500 resize-none bg-slate-50 placeholder:text-slate-400" data-id="element-2899" />
          <div className="flex items-center justify-end gap-2 mt-2" data-id="element-2900">
            {notesSaved && <span className="flex items-center gap-1 text-xs text-emerald-600 font-medium" data-id="element-2901">
                <Check className="h-3.5 w-3.5" data-id="element-2902" />
                Saved!
              </span>}
            <Button variant="outline" size="sm" onClick={handleSaveNotes} leftIcon={<Save className="h-3.5 w-3.5" data-id="element-2904" />} data-id="element-2903">
              Save Notes
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>;
}
export function ProjectDetailPage() {
  const {
    projectId
  } = useParams();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<TabId>('info');
  const project = projectData[projectId || '1'];
  if (!project) return <div className="p-8 text-center" data-id="element-2905">
        <p className="text-slate-500" data-id="element-2906">Project not found</p>
        <Button onClick={() => navigate('/admin/projects')} className="mt-4" data-id="element-2907">
          Back to Projects
        </Button>
      </div>;
  return <div className="space-y-6 max-w-7xl mx-auto" data-id="element-2908">
      <div className="flex items-center justify-between" data-id="element-2909">
        <div className="flex items-center gap-4" data-id="element-2910">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)} leftIcon={<ArrowLeft className="h-4 w-4" data-id="element-2912" />} data-id="element-2911">
            Back
          </Button>
          <div data-id="element-2913">
            <div className="flex items-center gap-3" data-id="element-2914">
              <h1 className="text-2xl font-bold text-slate-900" data-id="element-2915">
                {project.name}
              </h1>
              <Badge variant={project.status === 'Active' ? 'success' : 'neutral'} data-id="element-2916">
                {project.status}
              </Badge>
            </div>
            <div className="flex items-center gap-4 mt-1.5 text-sm text-slate-500" data-id="element-2917">
              <div className="flex items-center gap-1" data-id="element-2918">
                <Building2 className="h-3.5 w-3.5" data-id="element-2919" />
                <button onClick={() => navigate(`/admin/builders/${project.builderId}`)} className="hover:text-primary-600 transition-colors" data-id="element-2920">
                  {project.builder}
                </button>
              </div>
              <div className="flex items-center gap-1" data-id="element-2921">
                <Calendar className="h-3.5 w-3.5" data-id="element-2922" />
                Created {new Date(project.createdDate).toLocaleDateString()}
              </div>
              <div className="flex items-center gap-1" data-id="element-2923">
                <Activity className="h-3.5 w-3.5" data-id="element-2924" />
                Last activity {project.lastActivity}
              </div>
            </div>
          </div>
        </div>
        <Button variant="outline" onClick={() => navigate('/options/models')} data-id="element-2925">
          Manage Options
        </Button>
      </div>
      <div className="flex gap-6" data-id="element-2926">
        <div className="w-64 flex-shrink-0" data-id="element-2927">
          <Card className="overflow-hidden" noPadding data-id="element-2928">
            <div className="p-4 border-b border-slate-200" data-id="element-2929">
              <h2 className="font-semibold text-slate-900" data-id="element-2930">Project Settings</h2>
              <p className="text-xs text-slate-500 mt-0.5" data-id="element-2931">
                View & update data
              </p>
            </div>
            <nav className="p-2" data-id="element-2932">
              {tabs.map(tab => {
              const Icon = tab.icon;
              const isActive = activeTab === tab.id;
              return <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`w-full flex items-start gap-3 px-3 py-2.5 rounded-md text-sm transition-colors text-left ${isActive ? 'bg-primary-50 text-primary-700' : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'}`} data-id="element-2933">
                    <Icon className={`h-4 w-4 mt-0.5 flex-shrink-0 ${isActive ? 'text-primary-600' : 'text-slate-400'}`} data-id="element-2934" />
                    <div data-id="element-2935">
                      <div className={`font-medium leading-tight ${isActive ? 'text-primary-700' : 'text-slate-800'}`} data-id="element-2936">
                        {tab.label}
                      </div>
                      <div className="text-xs text-slate-500 mt-0.5" data-id="element-2937">
                        {tab.subtitle}
                      </div>
                    </div>
                  </button>;
            })}
            </nav>
          </Card>
        </div>
        <div className="flex-1 min-w-0" data-id="element-2938">
          {activeTab === 'info' && <InfoTab project={project} data-id="element-2939" />}
          {activeTab === 'models' && <ModelsTab data-id="element-2940" />}
          {activeTab === 'media' && <MediaTab data-id="element-2941" />}
          {activeTab === 'documents' && <DocumentsTab data-id="element-2942" />}
          {activeTab === 'notes' && <NotesTab data-id="element-2943" />}
        </div>
      </div>
    </div>;
}